import React, { useMemo } from 'react';
import Image from 'next/image';

import { slugify } from '@blockworks/platform/utils';
import { DateFormat } from '@blockworks/ui/date-time';
import { Link } from '@blockworks/ui/legacy/link';
import { RenderSimpleHtml } from '@blockworks/ui/legacy/string';

import ChevronBackIcon from '@/assets/icons/chevron-back-icon';
import ApplePodcastIcon from '@/components/pages/podcast/components/apple-podcast-icon';
import PlayPauseIcon from '@/components/pages/podcast/components/play-pause-icon';
import SpotifyIcon from '@/components/pages/podcast/components/spotify-icon';
import { useAudioPlayer } from '@/components/pages/podcast/layout/audio-provider';
import Episode from '@/types/pages/api/podcasts/episode';

import EpisodeLinks from './episode-links/episode-links';

type EpisodeHeadProps = {
    episode: Episode;
    spotifyLink: string;
    appleLink: string;
    podcastTitle: string;
    podcastSlug: string;
    imageUrl: string | null;
};

const EpisodeHead = (props: EpisodeHeadProps) => {
    const { episode, podcastTitle, imageUrl, spotifyLink, appleLink, podcastSlug } = props;

    // const summarySplit = episode.summary.split('- -');
    // const summary = summarySplit.length > 0 ? summarySplit[0] : episode.summary;
    const { summary } = episode;

    const audioPlayerData = useMemo(
        () => ({
            title: episode.title,
            show: episode.podcastTitle,
            audio: {
                src: episode.downloadUrl,
                type: 'mp3',
            },
            link: `/podcast/${slugify(episode.title)}/${episode.id}`,
        }),
        [episode],
    );
    const player = useAudioPlayer(audioPlayerData);

    return (
        <div
            className="gap-4 lg:gap-8 grid grid-cols-1 md:grid-cols-[220px_1fr] lg:grid-cols-[300px_1fr] md:space-x-10 items-center justify-start p-6 md:p-8 lg:p-10"
            itemScope
            itemType="https://schema.org/AudioObject"
        >
            <meta itemProp="contentUrl" content={episode.downloadUrl} />
            <div className="display-none md:block w-full h-full lg:h-full">
                {imageUrl && (
                    <Image
                        className="w-full"
                        src={imageUrl}
                        width={512}
                        height={512}
                        alt=""
                        sizes="(min-width: 1024px) 20rem, (min-width: 640px) 16rem, 12rem"
                        itemProp="image"
                        priority
                    />
                )}
            </div>
            <div className="flex flex-col space-y-3 items-start justify-center">
                <div className="flex flex-col space-y-2 items-start justify-start w-full">
                    <div className="inline-flex space-x-2 items-center justify-start">
                        <div className="flex space-x-2 items-center justify-start">
                            <p className="text-xs font-buch tracking-widest leading-none text-indigo-700 uppercase">
                                <Link href="/">home</Link>
                            </p>
                            <ChevronBackIcon width={4} height={7} />
                            <p className="text-xs font-buch tracking-widest leading-none text-indigo-700 uppercase">
                                <Link href="/podcasts">podcasts</Link>
                            </p>
                            <ChevronBackIcon width={4} height={7} />
                            <p className="text-xs font-buch tracking-widest leading-none text-indigo-700 uppercase">
                                <Link href={`/podcast/${podcastSlug}`}>{podcastTitle}</Link>
                            </p>
                        </div>
                        <div className="flex space-x-1 items-center justify-start">
                            <div className="flex space-x-2 items-center justify-start">
                                <p className="text-xs font-buch tracking-widest leading-none text-gray-500 uppercase">
                                    ep. {episode.episodeNumber}
                                </p>
                            </div>
                            <div className="flex space-x-2 items-center justify-start align-center">
                                <p className="text-xs text-gray-500 uppercase">
                                    / <DateFormat date={episode.pubdate} />
                                </p>
                            </div>
                        </div>
                    </div>
                    <p
                        className="text-4xl text-[28px] lg:text-[36] font-semibold font-tiempos max-w-[800px] leading-10 text-gray-900"
                        itemProp="name"
                    >
                        {episode.title}
                    </p>
                    <a
                        onClick={() => player.toggle()}
                        className="flex items-center text-sm font-bold leading-6 text-brand hover:text-gray-700 active:text-gray-900 cursor-pointer"
                        aria-label={`${player.playing ? 'Pause' : 'Play'} episode ${episode.title}`}
                    >
                        <PlayPauseIcon playing={player.playing} className="h-2.5 w-2.5 fill-current" />
                        <span className="ml-3" aria-hidden="true">
                            {player.playing ? 'Pause' : 'Listen'}
                        </span>
                    </a>
                </div>
                <p className="text-base font-buch leading-relaxed text-gray-900" itemProp="description">
                    <RenderSimpleHtml html={summary || ''} />
                </p>
                <EpisodeLinks
                    episode={episode}
                    links={[
                        // @ts-ignore
                        ['Spotify', SpotifyIcon, spotifyLink || ''],
                        // @ts-ignore
                        ['Apple Podcast', ApplePodcastIcon, appleLink || ''],
                    ]}
                />
            </div>
        </div>
    );
};

export default EpisodeHead;
